// Bootstrap React app. Mounts <App /> wrapped in QueryClientProvider.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App.js';
import { ToastProvider } from './components/Toast.js';
import { is401Error } from './api/client.js';
import { isProdHost } from './lib/env.js';
import './index.css';

// Dev-environment favicon + title swap. Detection is hostname-based so the
// same code bundle differentiates DEV vs PROD at the browser-tab level
// (tab favicon + title), even when the bundle is identical. Anything that
// isn't the canonical prod hostname is treated as DEV — covers localhost,
// LAN IPs, and any preview deployments.
//
// "[DEV] " is prefixed to the title (rather than appended) because browser
// tabs truncate from the right; "Inventory & Crew Management" is long
// enough that a suffix would get clipped on narrow tabs.
if (typeof window !== 'undefined' && !isProdHost()) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) link.href = '/favicon-dev.svg';
  document.title = `[DEV] ${document.title}`;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry 401s at the react-query layer. The fetch layer
        // (fetchWithAuthRetry in api/client.ts) already retries a 401 ONCE to
        // ride a sibling request's token refresh; a 401 that reaches here has
        // survived that retry, so it's a real auth boundary → bounce to Login.
        if (is401Error(error)) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      // Pause polling in hidden/backgrounded tabs (global default). The free-tier
      // Supabase pooler has ~15 shared tx slots; a forgotten/idle tab shouldn't
      // hold one open on every refetchInterval drip. Per-query polling cadence is
      // still set per call site, but background behavior is policy and lives here
      // (2026-06-11 — CF 1102 "Worker exceeded resource limits" mitigation).
      refetchIntervalInBackground: false,
    },
  },
});

// Global 401 handler: when ANY non-me query errors with a 401, invalidate
// the me query so App.tsx re-renders and bounces to <Login />. Without
// this, a session that expires mid-session leaves the stale UI rendered
// until the next staleTime tick.
//
// Skip when the failing query IS the me query itself — getMe throws on
// 401 (rather than returning null like Cambonic's fetchMe), so a 401
// on me would otherwise infinite-loop here: invalidate → refetch → 401
// → invalidate → ... App.tsx's own retry policy + error branch already
// handle the me-query 401 case by rendering <Login />, so this
// subscriber only needs to handle OTHER queries' 401s.
queryClient.getQueryCache().subscribe((event) => {
  if (
    event.type !== 'updated' ||
    event.action.type !== 'error' ||
    !is401Error(event.action.error)
  ) {
    return;
  }
  const key = event.query.queryKey;
  if (Array.isArray(key) && key[0] === 'me') return;
  void queryClient.invalidateQueries({ queryKey: ['me'] });
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('root element not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <App />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
